// Group-vote rules, for DISPLAY only (Part 2, spec §2 Votes, contract §4.6).
//
// The server decides every vote: who is eligible, the threshold, when it may
// pass. The phone cannot see presence (session_seat_presence is private), so
// anything computed here is an estimate that assumes every active seat is
// awake. It exists so the sheet can say "3 of 5 votes needed" before a vote is
// opened; once one is open, the VoteView the server returns is the truth and
// the UI shows that instead.

/** Seconds a vote may sit past expires_at and still be resolved (C1). */
export const LATE_RESOLVE_GRACE_MS = 60_000;

/**
 * Yes votes a normal-mode vote needs: a strict majority of the n eligible
 * voters, and at least 2 once the table has 3 or more active seats.
 */
export function requiredYes(eligibleCount, activeSeatCount) {
  const n = Math.max(0, Number(eligibleCount) || 0);
  const minYes = Number(activeSeatCount) >= 3 ? 2 : 1;
  return Math.max(Math.floor(n / 2) + 1, minYes);
}

/**
 * Estimate a vote before it is opened, assuming every active seat is awake.
 *   kind:     'gm_handover' | 'remove_player'
 *   activeIds: ids of active seats
 *   gmId:     current GM seat id (or null)
 *   targetId: the candidate (handover) or the seat to remove
 * Returns { eligible, required, canPass }.
 */
export function estimateVote({ kind, activeIds, gmId = null, targetId = null }) {
  const active = [...(activeIds || [])];
  const eligible = active.filter((id) => {
    if (kind === 'remove_player' && id === targetId) return false;
    if (kind === 'gm_handover' && id === gmId) return false;
    return true;
  }).length;
  const required = requiredYes(eligible, active.length);
  return { eligible, required, canPass: required <= eligible };
}

/**
 * Clock skew between this phone and the server, from a response's server_now.
 * Add it to Date.now() to get the server's clock.
 */
export function clockOffsetFrom(serverNowIso, receivedAt = Date.now()) {
  const server = Date.parse(serverNowIso || '');
  if (!Number.isFinite(server)) return 0;
  return server - receivedAt;
}

/** Milliseconds from now (server clock) until an ISO moment. Negative once past. */
export function msUntil(iso, offset = 0, now = Date.now()) {
  const at = Date.parse(iso || '');
  if (!Number.isFinite(at)) return null;
  return at - (now + offset);
}

/** "3:12" — never negative. */
export function formatCountdown(ms) {
  const total = Math.max(0, Math.ceil((Number(ms) || 0) / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function isVoteOpen(vote) {
  return Boolean(vote) && vote.status === 'open';
}

/**
 * Should this phone call resolve_session_vote right now? True when the server
 * already says it is due (or overdue), or when expires_at has passed on the
 * server clock. The call is idempotent; this only decides whether it is worth
 * making (D1: on mount and on wake).
 */
export function isVoteDue(vote, offset = 0, now = Date.now()) {
  if (!isVoteOpen(vote)) return false;
  if (vote.effective_status === 'due' || vote.effective_status === 'expired') return true;
  const toExpiry = msUntil(vote.expires_at, offset, now);
  return toExpiry !== null && toExpiry <= 0;
}

/**
 * Future moments at which this phone should call resolve_session_vote: the
 * 90-second handover minimum (min_pass_at) and expiry. Returns ms delays,
 * soonest first, only for moments still ahead.
 */
export function resolveDelays(vote, offset = 0, now = Date.now()) {
  if (!isVoteOpen(vote)) return [];
  const out = [];
  for (const iso of [vote.min_pass_at, vote.expires_at]) {
    const ms = msUntil(iso, offset, now);
    if (ms !== null && ms > 0) out.push(ms);
  }
  return [...new Set(out)].sort((a, b) => a - b);
}

function nameOf(names, id, fallback = 'Someone') {
  if (!id) return fallback;
  return names?.[id] || fallback;
}

/**
 * The sentence a vote shows. `viewerId` changes the wording for the candidate
 * of a direct transfer ("Alex wants you to be the game master. Accept?").
 */
export function voteHeadline(vote, { viewerId = null, names = {} } = {}) {
  if (!vote) return '';
  const starter = vote.starter_name || nameOf(names, vote.started_by);
  const target = vote.target_name || nameOf(names, vote.target_player_id, 'this player');
  if (vote.kind === 'gm_transfer') {
    if (viewerId && viewerId === vote.target_player_id) {
      return `${starter} wants you to be the game master. Accept?`;
    }
    return `${starter} wants ${target} to be the game master`;
  }
  if (vote.kind === 'gm_handover') {
    if (vote.started_by === vote.target_player_id) {
      return `${starter} wants to become the game master`;
    }
    return `${starter} wants ${target} to become the game master`;
  }
  if (vote.kind === 'remove_player') return `Vote to mark ${target} as away`;
  return 'A vote is open';
}

/** The short form the banner uses: "Vote: make Sam the game master". */
export function voteBannerLabel(vote, { viewerId = null, names = {} } = {}) {
  if (!vote) return '';
  const target = vote.target_name || nameOf(names, vote.target_player_id, 'this player');
  if (vote.kind === 'gm_transfer') {
    return viewerId && viewerId === vote.target_player_id
      ? 'You’re asked to be the game master'
      : `Handing game master to ${target}`;
  }
  if (vote.kind === 'gm_handover') return `Vote: make ${target} the game master`;
  if (vote.kind === 'remove_player') return `Vote: mark ${target} as away`;
  return 'Vote open';
}

/** "2 of 3 votes needed" from a server VoteView. */
export function tallyLine(vote) {
  if (!vote) return '';
  if (vote.kind === 'gm_transfer') return 'Needs their yes';
  const needed = Number(vote.threshold) || 0;
  const of = Number(vote.eligible_count) || 0;
  return `${needed} of ${of} votes needed`;
}

/** Plain copy for a vote that has just closed. */
export function resolvedLine(vote, { names = {} } = {}) {
  if (!vote) return '';
  const target = vote.target_name || nameOf(names, vote.target_player_id, 'this player');
  switch (vote.status) {
    case 'passed':
      if (vote.kind === 'remove_player') return `${target} is marked away.`;
      return `${target} is now the game master.`;
    case 'vetoed':
      return 'The game master said no, so nothing changed.';
    case 'failed':
      return 'Not enough yes votes, so nothing changed.';
    case 'expired':
      return 'The vote ran out of time, so nothing changed.';
    case 'cancelled':
      return 'The vote was cancelled.';
    case 'superseded':
      return 'The vote closed because the table changed.';
    default:
      return '';
  }
}
